import { useProfile } from '@ninetailed/experience.js-next';
import { CountryCode } from 'config/constants/locales';
import { useAuth } from 'contexts/auth';

interface UseUserCountryOutput {
  /** The detected country code (DE, AT, or CH) */
  countryCode: CountryCode;
  /** Whether the user is detected as being in Austria */
  isAustria: boolean;
  /** Whether the country detection is still loading */
  loading: boolean;
}

const countryCodeValues: string[] = Object.values(CountryCode);

const isValidCountryCode = (code: string | undefined): code is CountryCode =>
  code !== undefined && countryCodeValues.includes(code);

/**
 * Hook to detect the user's country based on shipping address or geo-location.
 * Priority: shipping address > geo-location > default (DE)
 */
const useUserCountry = (): UseUserCountryOutput => {
  const { shippingAddress } = useAuth();
  const { profile, loading } = useProfile();

  const geoCountryCode = profile?.location?.countryCode;
  const shippingCountryCode = shippingAddress?.countryCode;

  // Priority: shipping address > geo-location > default (DE)
  const getCountryCode = (): CountryCode => {
    if (isValidCountryCode(shippingCountryCode)) return shippingCountryCode;
    if (isValidCountryCode(geoCountryCode)) return geoCountryCode;
    return CountryCode.DE;
  };

  const countryCode = getCountryCode();
  const isAustria = countryCode === CountryCode.AT;

  return {
    countryCode,
    isAustria,
    loading,
  };
};

export default useUserCountry;
